import { useCallback } from 'react';
import { useHumanInTheLoop } from '@copilotkit/react-core';
import { useNavigate } from 'react-router-dom';

import { TOOL_NAMES } from '@/helpers/aiChat/tools';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import useIntegrationAvailabilities from '@/hooks/useIntegrationAvailabilities';
import { useIsAppLimitReached } from '@/hooks/useIsAppLimitReached';
import { useLazyGetApplicationsQuery } from '@/redux/slices/applicationApiSlice';
import { useAddOrganizationApplicationMutation } from '@/redux/slices/organizationApiSlice';
import { ConnectAppIntegrationToolRenderer } from './ConnectAppIntegrationToolRenderer';

export type HandleConnectResult = { name: string; integrationOpened: boolean };

export const useConnectAppIntegrationTool = () => {
  const navigate = useNavigate();
  const organizationId = useGetOrganizationId();
  const availableIntegrations = useIntegrationAvailabilities();
  const isAppLimitReached = useIsAppLimitReached();
  const [fetchApplications] = useLazyGetApplicationsQuery();
  const [addOrganizationApplication] = useAddOrganizationApplicationMutation();

  const handleConnect = useCallback(
    async (
      appName: string,
      existingSlug?: string,
      hasIntegration?: boolean
    ): Promise<HandleConnectResult> => {
      if (!organizationId) {
        throw new Error(
          'Organization not found. Please refresh and try again.'
        );
      }

      if (isAppLimitReached) {
        throw new Error(
          'Your organization has reached its automated integration limit. Please contact support to connect additional integrations.'
        );
      }

      if (existingSlug) {
        const integrationOpened = hasIntegration ?? false;
        navigate(`/applications/${existingSlug}`, {
          state: { openIntegrationModal: integrationOpened },
        });
        return { name: appName, integrationOpened };
      }

      const { data: catalogData } = await fetchApplications({ name: appName });
      const catalogApp = catalogData?.applications?.[0];

      if (!catalogApp) {
        throw new Error(
          `Could not find "${appName}" in the application catalog. Please check the name and try again.`
        );
      }

      await addOrganizationApplication({
        organizationId,
        application: { applicationId: catalogApp.id },
      }).unwrap();

      const integrationOpened = availableIntegrations.includes(
        catalogApp.integrationAvailability
      );

      navigate(`/applications/${catalogApp.slug}`, {
        state: { openIntegrationModal: integrationOpened },
      });

      return { name: catalogApp.name, integrationOpened };
    },
    [
      organizationId,
      isAppLimitReached,
      navigate,
      availableIntegrations,
      fetchApplications,
      addOrganizationApplication,
    ]
  );

  const renderTool = useCallback(
    (props: {
      args: Record<string, unknown>;
      status: string;
      respond?: (result: string) => void;
    }) => (
      <ConnectAppIntegrationToolRenderer
        {...props}
        handleConnect={handleConnect}
      />
    ),
    [handleConnect]
  );

  useHumanInTheLoop({
    name: TOOL_NAMES.CONNECT_APP_INTEGRATION,
    description:
      'Use this tool when the admin wants to connect an app integration OR add an application to the organization. If the organization does not have the application yet, it will be added automatically. If the application supports integration, it will navigate to the application page and trigger the integration connection flow. If the application does not support integration, it will navigate to the application page.',
    parameters: [
      {
        name: 'app_name',
        type: 'string',
        description:
          'The name of the application to add or navigate to (e.g., "Google Workspace", "Microsoft 365", "Slack", "Trello").',
        required: true,
      },
    ],
    render: renderTool,
  });
};
